import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, FileText, Plus, TrendingUp, CheckCircle } from 'lucide-react';
import { getBillingOverview, getInvoices, createInvoice, generateMonthlyInvoices } from '../api';

export default function Billing() {
  const [overview, setOverview] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [form, setForm] = useState({ school_id: '', amount: '', description: '', due_date: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    getBillingOverview().then(r => setOverview(r.data)).catch(() => {});
    getInvoices().then(r => setInvoices(r.data.results || r.data || [])).catch(() => {});
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await createInvoice(form);
      setShowCreate(false);
      setForm({ school_id: '', amount: '', description: '', due_date: '' });
      getInvoices().then(r => setInvoices(r.data.results || r.data || []));
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  const handleGenerate = async () => {
    setGenLoading(true);
    try {
      await generateMonthlyInvoices();
      getInvoices().then(r => setInvoices(r.data.results || r.data || []));
      getBillingOverview().then(r => setOverview(r.data));
    } catch (e) { console.error(e); }
    finally { setGenLoading(false); }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Billing</h2>
        <p>Revenue tracking and invoice management</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon blue"><DollarSign size={22} /></div>
          <div className="stat-label">Monthly Revenue</div>
          <div className="stat-value blue">${overview?.monthly_revenue || 0}</div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon green"><CheckCircle size={22} /></div>
          <div className="stat-label">Paid</div>
          <div className="stat-value green">${overview?.total_paid || 0}</div>
        </div>
        <div className="stat-card yellow">
          <div className="stat-icon" style={{ background: 'var(--yellow-bg)', color: 'var(--yellow)' }}>
            <FileText size={22} />
          </div>
          <div className="stat-label">Outstanding</div>
          <div className="stat-value" style={{ color: 'var(--yellow)' }}>${overview?.total_outstanding || 0}</div>
        </div>
        <div className="stat-card accent">
          <div className="stat-icon accent"><TrendingUp size={22} /></div>
          <div className="stat-label">Total Invoices</div>
          <div className="stat-value accent">{overview?.total_invoices || invoices.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3><CreditCard size={18} /> Invoices</h3>
          <div className="btn-group">
            <button className="btn btn-secondary btn-sm" onClick={handleGenerate} disabled={genLoading}>
              <FileText size={14} /> {genLoading ? 'Generating...' : 'Generate Monthly'}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(!showCreate)}>
              <Plus size={14} /> New Invoice
            </button>
          </div>
        </div>

        {showCreate && (
          <div className="card-body" style={{ borderBottom: '1px solid var(--border)' }}>
            <form onSubmit={handleCreate}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">School ID</label>
                  <input className="form-input" value={form.school_id} onChange={e => setForm({ ...form, school_id: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount ($)</label>
                  <input className="form-input" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input className="form-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>
              <div className="btn-group" style={{ marginTop: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Invoice'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowCreate(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>School</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td className="font-mono text-sm">#{inv.id}</td>
                  <td><strong className="text-sm">{inv.school_name || inv.school_id}</strong></td>
                  <td className="font-mono">${inv.amount}</td>
                  <td><span className={`badge ${inv.status}`}>{inv.status}</span></td>
                  <td className="font-mono text-sm text-muted">{inv.due_date || '—'}</td>
                  <td className="text-sm text-secondary">{inv.description?.substring(0, 50) || '—'}</td>
                </tr>
              ))}
              {invoices.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <CreditCard size={40} />
                      <p>No invoices yet</p>
                      <span className="sub">Generate monthly invoices or create one manually</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
